import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiVerifyIdentityData,
  type EnrollmentMultiVerifyIdentitySubmitInput,
  type EnrollmentMultiVerifyIdentitySubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi VerifyIdentity data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiVerifyIdentityData>
 * @example const response = await getEnrollmentMultiVerifyIdentityData()
 */
export const getEnrollmentMultiVerifyIdentityData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiVerifyIdentityData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiVerifyIdentityData',
      query: `mutation getEnrollmentMultiVerifyIdentityData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              progress
              dateOfBirth
              residencyStatus
              citizenshipCountries
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiVerifyIdentityData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiVerifyIdentityData = createQueryHook<undefined, EnrollmentMultiVerifyIdentityData>(
  getEnrollmentMultiVerifyIdentityData,
)

/**
 * Submit Enrollment Multi VerifyIdentity data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiVerifyIdentitySubmitOutput>
 * @example const response = await submitEnrollmentMultiVerifyIdentityData();
 */
export const submitEnrollmentMultiVerifyIdentityData = async (
  enrollmentVerifyIdentitySubmitInput: EnrollmentMultiVerifyIdentitySubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiVerifyIdentitySubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiVerifyIdentityData',
      query: `mutation submitEnrollmentMultiVerifyIdentityData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              maskedSsn
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentVerifyIdentitySubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiVerifyIdentitySubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiVerifyIdentityData = createMutationHook<
  EnrollmentMultiVerifyIdentitySubmitInput,
  EnrollmentMultiVerifyIdentitySubmitOutput
>(submitEnrollmentMultiVerifyIdentityData)
